import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../../services/api';
import { getSubdomainUsername, getUsernameSyncOnly } from '../../../utils/subdomain';
import { buildMediaUrl } from '../../../utils/mediaUrl';

const PublicAboutSection = () => {
  const { username: urlUsername } = useParams();
  const subdomainUser = getSubdomainUsername();
  const cachedCustomDomainUser = getUsernameSyncOnly();
  const username = urlUsername || subdomainUser || cachedCustomDomainUser;

  const [about, setAbout] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAbout = async () => {
      try {
        const res = await api.get(`/business/about/${username}/`);
        setAbout(res.data);
      } catch (err) {
        console.error('Error fetching about section:', err);
      } finally {
        setLoading(false);
      }
    };

    if (username) {
      fetchAbout();
    }
  }, [username]);

  if (loading) {
    return (
      <div style={{ padding: '4rem 2rem', textAlign: 'center' }}>
        <div style={{
          width: '2rem',
          height: '2rem',
          border: '3px solid rgba(255,255,255,0.1)',
          borderTopColor: '#6366f1',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto'
        }} />
      </div>
    );
  }

  if (!about) {
    return null;
  }

  const template = about.layout_variant || 'classic';
  const bgColor = about.background_color || '#0f172a';
  const textColor = about.text_color || '#ffffff';
  const isGradient = bgColor.includes('gradient');
  const bgStyle = isGradient ? { background: bgColor } : { backgroundColor: bgColor };

  // Hero Banner Template
  if (template === 'hero') {
    return (
      <div style={{ ...bgStyle, minHeight: '100vh' }}>
        {/* Hero Banner */}
        {about.hero_image && (
          <div style={{
            position: 'relative',
            height: '400px',
            overflow: 'hidden',
          }}>
            <img
              src={buildMediaUrl(about.hero_image)}
              alt="Hero"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
            <div style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to bottom, rgba(15,23,42,0.3), rgba(15,23,42,0.9))',
            }} />
            <div style={{
              position: 'absolute',
              bottom: '3rem',
              left: '3rem',
              right: '3rem',
            }}>
              <h1 style={{
                fontSize: '3rem',
                fontWeight: '700',
                color: textColor,
              }}>
                {about.title || 'About Us'}
              </h1>
              {about.subtitle && (
                <p style={{ color: textColor, opacity: 0.8, marginTop: '0.75rem', fontSize: '1.25rem' }}>
                  {about.subtitle}
                </p>
              )}
            </div>
          </div>
        )}

        <div style={{ padding: '3rem', maxWidth: '1200px', margin: '0 auto' }}>
          {/* Founder Section */}
          {(about.founder_name || about.founder_image) && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1.5rem',
              marginBottom: '2.5rem',
              padding: '1.5rem',
              backgroundColor: 'rgba(255,255,255,0.05)',
              borderRadius: '1rem',
            }}>
              {about.founder_image && (
                <img
                  src={buildMediaUrl(about.founder_image)}
                  alt={about.founder_name}
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                  }}
                />
              )}
              <div>
                <h3 style={{ color: textColor, fontWeight: '600', fontSize: '1.25rem' }}>
                  {about.founder_name}
                </h3>
                {about.founder_title && (
                  <p style={{ color: '#6366f1', fontSize: '1rem' }}>
                    {about.founder_title}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Content */}
          {about.content && (
            <p style={{
              color: textColor,
              opacity: 0.8,
              fontSize: '1.1rem',
              lineHeight: '1.8',
              marginBottom: '2.5rem',
            }}>
              {about.content}
            </p>
          )}

          {/* Mission & Vision */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2.5rem' }}>
            {about.mission && (
              <div style={{
                padding: '1.5rem',
                backgroundColor: 'rgba(99, 102, 241, 0.1)',
                borderRadius: '0.75rem',
                borderLeft: '4px solid #6366f1',
              }}>
                <h4 style={{ color: '#6366f1', fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Mission
                </h4>
                <p style={{ color: textColor, opacity: 0.7, fontSize: '0.95rem', lineHeight: '1.6' }}>
                  {about.mission}
                </p>
              </div>
            )}
            {about.vision && (
              <div style={{
                padding: '1.5rem',
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                borderRadius: '0.75rem',
                borderLeft: '4px solid #8b5cf6',
              }}>
                <h4 style={{ color: '#8b5cf6', fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Vision
                </h4>
                <p style={{ color: textColor, opacity: 0.7, fontSize: '0.95rem', lineHeight: '1.6' }}>
                  {about.vision}
                </p>
              </div>
            )}
          </div>

          {/* CTA Button */}
          {about.cta_text && (
            <a
              href={about.cta_link || '#'}
              style={{
                display: 'inline-block',
                padding: '1rem 2rem',
                backgroundColor: '#6366f1',
                color: '#fff',
                border: 'none',
                borderRadius: '0.5rem',
                fontWeight: '600',
                textDecoration: 'none',
                cursor: 'pointer',
              }}
            >
              {about.cta_text}
            </a>
          )}
        </div>
      </div>
    );
  }

  // Story Template
  if (template === 'story') {
    return (
      <div style={{ ...bgStyle, minHeight: '100vh', padding: '4rem 2rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 style={{
              fontSize: '2.5rem',
              fontWeight: '700',
              color: textColor,
              marginBottom: '0.75rem',
            }}>
              {about.title || 'Our Story'}
            </h2>
            {about.subtitle && (
              <p style={{ color: textColor, opacity: 0.6, fontSize: '1.1rem' }}>
                {about.subtitle}
              </p>
            )}
          </div>

          {/* Hero Image */}
          {about.hero_image && (
            <div style={{
              borderRadius: '1rem',
              overflow: 'hidden',
              marginBottom: '2.5rem',
            }}>
              <img
                src={buildMediaUrl(about.hero_image)}
                alt="Story"
                style={{ width: '100%', height: 'auto' }}
              />
            </div>
          )}

          {/* Main Content */}
          {about.content && (
            <p style={{
              color: textColor,
              opacity: 0.8,
              fontSize: '1.1rem',
              lineHeight: '1.8',
              marginBottom: '3rem',
            }}>
              {about.content}
            </p>
          )}

          {/* Story Section */}
          {about.story_title && (
            <div style={{
              padding: '2rem',
              backgroundColor: 'rgba(99, 102, 241, 0.1)',
              borderRadius: '1rem',
              borderLeft: '4px solid #6366f1',
              marginBottom: '2.5rem',
            }}>
              <h3 style={{
                color: textColor,
                fontSize: '1.25rem',
                fontWeight: '600',
                marginBottom: '1rem',
              }}>
                {about.story_title}
              </h3>
              {about.story_content && (
                <p style={{
                  color: textColor,
                  opacity: 0.7,
                  fontSize: '1rem',
                  lineHeight: '1.8',
                }}>
                  {about.story_content}
                </p>
              )}
            </div>
          )}

          {/* Mission & Vision */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            {about.mission && (
              <div style={{
                padding: '1.5rem',
                backgroundColor: 'rgba(99, 102, 241, 0.1)',
                borderRadius: '0.75rem',
                borderLeft: '4px solid #6366f1',
              }}>
                <h4 style={{ color: '#6366f1', fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Mission
                </h4>
                <p style={{ color: textColor, opacity: 0.7, fontSize: '0.95rem', lineHeight: '1.6' }}>
                  {about.mission}
                </p>
              </div>
            )}
            {about.vision && (
              <div style={{
                padding: '1.5rem',
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                borderRadius: '0.75rem',
                borderLeft: '4px solid #8b5cf6',
              }}>
                <h4 style={{ color: '#8b5cf6', fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Vision
                </h4>
                <p style={{ color: textColor, opacity: 0.7, fontSize: '0.95rem', lineHeight: '1.6' }}>
                  {about.vision}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Classic Template (default)
  return (
    <div style={{
      ...bgStyle,
      padding: '4rem 2rem',
      minHeight: '50vh',
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: '700',
            color: textColor,
            marginBottom: '0.5rem',
          }}>
            {about.title || 'About Us'}
          </h2>
          {about.subtitle && (
            <p style={{ color: textColor, opacity: 0.6, fontSize: '1.1rem' }}>
              {about.subtitle}
            </p>
          )}
        </div>

        {/* Content with Image */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: about.image ? '1fr 1fr' : '1fr',
          gap: '3rem',
          alignItems: 'start',
        }}>
          {about.image && about.image_position === 'left' && (
            <div style={{
              borderRadius: '1rem',
              overflow: 'hidden',
            }}>
              <img
                src={buildMediaUrl(about.image)}
                alt={about.title}
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                }}
              />
            </div>
          )}

          <div>
            {about.content && (
              <p style={{
                color: textColor,
                opacity: 0.8,
                fontSize: '1.1rem',
                lineHeight: '1.8',
                marginBottom: '2rem',
              }}>
                {about.content}
              </p>
            )}

            {/* Mission & Vision */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1.5rem',
            }}>
              {about.mission && (
                <div style={{
                  padding: '1.5rem',
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                  borderRadius: '0.75rem',
                  borderLeft: '4px solid #6366f1',
                }}>
                  <h3 style={{
                    color: '#6366f1',
                    fontWeight: '600',
                    marginBottom: '0.5rem',
                    fontSize: '1rem',
                  }}>
                    Our Mission
                  </h3>
                  <p style={{ color: textColor, opacity: 0.7, fontSize: '0.95rem', lineHeight: '1.6' }}>
                    {about.mission}
                  </p>
                </div>
              )}

              {about.vision && (
                <div style={{
                  padding: '1.5rem',
                  backgroundColor: 'rgba(139, 92, 246, 0.1)',
                  borderRadius: '0.75rem',
                  borderLeft: '4px solid #8b5cf6',
                }}>
                  <h3 style={{
                    color: '#8b5cf6',
                    fontWeight: '600',
                    marginBottom: '0.5rem',
                    fontSize: '1rem',
                  }}>
                    Our Vision
                  </h3>
                  <p style={{ color: textColor, opacity: 0.7, fontSize: '0.95rem', lineHeight: '1.6' }}>
                    {about.vision}
                  </p>
                </div>
              )}
            </div>
          </div>

          {about.image && about.image_position !== 'left' && (
            <div style={{
              borderRadius: '1rem',
              overflow: 'hidden',
            }}>
              <img
                src={buildMediaUrl(about.image)}
                alt={about.title}
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicAboutSection;
